import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AdminIcon, FilterButton } from '../admin-kit';
import './organisatie.css';

export interface FilterOption<V extends string> {
  value: V;
  label: ReactNode;
}

/**
 * Een filterknop met een keuzemenu eronder (BHR-3): "Soort", "Toon: **Alleen aan**".
 *
 * De kit heeft de knop (`FilterButton`) maar niet wat er bij een klik opent; dat beslist het
 * scherm. In deel 03 is het drie keer hetzelfde - één waarde uit een korte lijst - dus staat het
 * hier één keer. Het menu gebruikt `.a-menu` van het avatarmenu, met `menuitemradio`.
 *
 * De eerste optie is "alles": staat die gekozen, dan is de knop niet actief en toont hij (zonder
 * `alwaysShowValue`) alleen zijn naam, zoals "Soort" in O1.
 */
export function FilterMenu<V extends string>({
  label,
  value,
  options,
  onChange,
  alwaysShowValue,
  testId,
}: {
  label: ReactNode;
  value: V;
  options: FilterOption<V>[];
  onChange: (value: V) => void;
  alwaysShowValue?: boolean;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = options.find((option) => option.value === value) ?? options[0];
  const isDefault = value === options[0]?.value;

  // Dicht bij een klik ernaast of Escape; de focus gaat dan terug naar de knop.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      ref.current?.querySelector<HTMLButtonElement>('.a-filter')?.focus();
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="org-filter" ref={ref}>
      <FilterButton
        label={label}
        value={alwaysShowValue || !isDefault ? current?.label : undefined}
        active={!isDefault}
        expanded={open}
        aria-haspopup="menu"
        testId={testId}
        onClick={() => setOpen((huidig) => !huidig)}
      />
      {open ? (
        <div className="a-menu org-filter-menu" role="menu">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={option.value === value}
              className="a-menu-item"
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
              {option.value === value ? (
                <span className="a-menu-right">
                  <AdminIcon name="check" size={16} />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
